import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASKS } from '../../api/endpoints';
import { toast } from 'react-toastify';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const priorityColor = (priority) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'success';
  return 'default';
};

const ViewTask = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(TASKS.BY_ID(id));
        const payload = data?.data ?? data;
        if (!cancelled) setTask(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task');
        navigate('/tasks');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !task) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const assignees = Array.isArray(task.assignees)
    ? task.assignees.map((u) => u?.name ?? u?.username).filter(Boolean).join(', ')
    : '';
  const observers = Array.isArray(task.observers)
    ? task.observers.map((u) => u?.name ?? u?.username).filter(Boolean).join(', ')
    : '';

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')} sx={{ mb: 2 }}>
          Back to tasks
        </Button>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {task.title ?? 'Task'}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate(`/tasks/edit/${id}`)}>
            Edit
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Status', task.status?.name ?? task.status?.label)}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Priority
                </Typography>
                <Chip
                  size="small"
                  label={task.priority ?? '—'}
                  color={priorityColor(task.priority)}
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              {row('Responsible', task.responsible?.name ?? task.responsible?.username)}
              {row('Lead', task.lead?.name ?? task.lead?.dba_name)}
              {row('Service', task.service?.name)}
              {row('Parent', task.parent?.title)}
              {row(
                'Deadline',
                task.deadline ? String(task.deadline).replace('T', ' ').slice(0, 16) : '—'
              )}
              {row('Save as template', task.save_as_template ? 'Yes' : 'No')}
              {row('Participants', assignees || '—')}
              {row('Observers', observers || '—')}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {task.description ?? '—'}
                </Typography>
              </Grid>
              {row('Author', task.author?.name ?? task.author?.username)}
              {row('Created at', task.created_at)}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewTask;
